<template>
    <div class="sse-test-container">
        <h1>SSE流式问答测试</h1>

        <!-- 问题输入区域 -->
        <div class="input-section">
            <div class="form-group">
                <label>请输入您的问题:</label>
                <textarea v-model="question" placeholder="请输入您的问题..." :disabled="isStreaming"></textarea>
            </div>

            <div class="form-group">
                <label>聊天ID (c_uuid):</label>
                <input v-model="chatId" placeholder="可选，留空则创建新聊天" :disabled="isStreaming" />
            </div>

            <div class="form-group">
                <label>请求方式:</label>
                <div class="radio-group">
                    <label>
                        <input type="radio" v-model="requestMethod" value="get" :disabled="isStreaming"> GET
                    </label>
                    <label>
                        <input type="radio" v-model="requestMethod" value="post" :disabled="isStreaming"> POST
                    </label>
                </div>
            </div>

            <div class="buttons">
                <button class="primary-btn" @click="startStream" :disabled="isStreaming || !question.trim()">
                    {{ isStreaming ? '接收中...' : '开始提问' }}
                </button>

                <button class="stop-btn" @click="stopStream" v-if="isStreaming">
                    停止接收
                </button>

                <button class="clear-btn" @click="clearMessages">
                    清空消息
                </button>
            </div>
        </div>

        <!-- 状态栏 -->
        <div class="status-bar">
            <div class="status-item">
                <strong>状态:</strong> <span>{{ connectionStatus }}</span>
            </div>
            <div class="status-item" v-if="startTime">
                <strong>已连接:</strong> <span>{{ elapsedTimeText }}</span>
            </div>
            <div class="status-item" v-if="currentAskId">
                <strong>问答ID:</strong> <span>{{ currentAskId }}</span>
            </div>
            <div class="status-item">
                <strong>请求方式:</strong> <span>{{ requestMethod.toUpperCase() }}</span>
            </div>
        </div>

        <!-- 消息显示区域 -->
        <div class="message-area">
            <div v-if="messages.length === 0" class="no-messages">
                请输入问题并点击"开始提问"按钮
            </div>

            <div class="messages" ref="messagesContainer">
                <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.type]">
                    <div class="message-header">
                        <span class="message-type">{{ getMessageTypeName(msg.type) }}</span>
                        <span class="message-time">{{ msg.time }}</span>
                    </div>
                    <div class="message-content markdown-body" v-html="renderContent(msg.content)"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { marked } from 'marked'
import 'katex/dist/katex.min.css'

// 配置marked，使用katex渲染数学公式
const renderer = {
    text(text) {
        console.log('renderer.text:', typeof text, text)
        if (typeof text === 'object' && text !== null) {
            return text.text || String(text)
        }
        return text
    },
    paragraph(text) {
        console.log('renderer.paragraph:', typeof text, text)
        if (typeof text === 'object' && text !== null) {
            return `<p>${text.text || String(text)}</p>`
        }
        return `<p>${text}</p>`
    },
    link(href, title, text) {
        if (typeof text === 'object' && text !== null) {
            text = text.text || String(text)
        }
        return `<a href="${href}"${title ? ` title="${title}"` : ''}>${text}</a>`
    },
    list(body, ordered) {
        return ordered ? `<ol>${body}</ol>` : `<ul>${body}</ul>`
    },
    listitem(text) {
        if (typeof text === 'object' && text !== null) {
            text = text.text || String(text)
        }
        return `<li>${text}</li>`
    },
    heading(text, level) {
        if (typeof text === 'object' && text !== null) {
            text = text.text || String(text)
        }
        return `<h${level}>${text}</h${level}>`
    },
    br() {
        return '<br>'
    },
    strong(text) {
        if (typeof text === 'object' && text !== null) {
            text = text.text || String(text)
        }
        return `<strong>${text}</strong>`
    },
    em(text) {
        if (typeof text === 'object' && text !== null) {
            text = text.text || String(text)
        }
        return `<em>${text}</em>`
    },
    codespan(text) {
        if (typeof text === 'object' && text !== null) {
            text = text.text || String(text)
        }
        return `<code>${text}</code>`
    },
    code(code) {
        if (typeof code === 'object' && code !== null) {
            code = code.text || String(code)
        }
        return `<pre><code>${code}</code></pre>`
    },
    blockquote(quote) {
        if (typeof quote === 'object' && quote !== null) {
            quote = quote.text || String(quote)
        }
        return `<blockquote>${quote}</blockquote>`
    },
    html(html) {
        return html
    },
    hr() {
        return '<hr>'
    },
    table(header, body) {
        return `<table><thead>${header}</thead><tbody>${body}</tbody></table>`
    },
    tablerow(content) {
        return `<tr>${content}</tr>`
    },
    tablecell(content, flags) {
        if (typeof content === 'object' && content !== null) {
            content = content.text || String(content)
        }
        const type = flags.header ? 'th' : 'td'
        return `<${type}>${content}</${type}>`
    },
    image(href, title, text) {
        if (typeof text === 'object' && text !== null) {
            text = text.text || String(text)
        }
        return `<img src="${href}" alt="${text}"${title ? ` title="${title}"` : ''}>`
    }
}

// 配置marked
marked.setOptions({
    renderer: renderer,
    highlight: null,
    pedantic: false,
    gfm: true,
    breaks: true,
    sanitize: false,
    smartypants: false,
    xhtml: false,
    headerIds: false,
    mangle: false
})

// 确保marked使用我们的renderer
marked.use({ renderer })

export default {
    name: 'SseTest',

    data() {
        return {
            // 用户输入
            question: '请给我介绍一下你自己',
            chatId: '',
            requestMethod: 'post', // 默认使用POST请求

            // 状态相关
            isStreaming: false,
            connectionStatus: '未连接',
            eventSource: null,
            startTime: null,
            timerInterval: null,
            currentAskId: '',

            // 消息相关
            messages: [],
            currentAnswer: '',

            // 基础URL
            apiBaseUrl: "https://easynoteai.online"
        }
    },

    computed: {
        // 计算已连接时间
        elapsedTimeText() {
            if (!this.startTime) return '0秒'
            const seconds = Math.floor((Date.now() - this.startTime) / 1000)

            if (seconds < 60) {
                return `${seconds}秒`
            } else {
                const minutes = Math.floor(seconds / 60)
                const remainingSeconds = seconds % 60
                return `${minutes}分${remainingSeconds}秒`
            }
        }
    },

    methods: {
        // 开始流式连接
        async startStream() {
            if (!this.question.trim() || this.isStreaming) return

            try {
                // 更新状态
                this.isStreaming = true
                this.connectionStatus = '连接中...'
                this.startTime = Date.now()
                this.startTimer()
                this.currentAnswer = ''
                this.currentAskId = ''

                // 防止缓存
                const timestamp = Date.now()

                // 定义端点
                const endpoint = 'sse/sendaskstream/' // 使用send_ask_stream端点
                // const endpoint = 'sse/test/' // 使用send_ask_stream_django端点

                // 添加系统消息
                this.addMessage('system', '连接服务器中...')

                // 创建初始回答消息
                this.addMessage('answer', '')

                if (this.requestMethod === 'get') {
                    // GET方式
                    await this.startStreamWithGet(endpoint, timestamp)
                } else {
                    // POST方式
                    await this.startStreamWithPost(endpoint, timestamp)
                }

            } catch (e) {
                console.error('启动流式连接错误:', e)
                this.addMessage('error', `错误: ${e.message}`)
                this.stopStream()
            }
        },

        // 使用GET方式请求SSE
        async startStreamWithGet(endpoint, timestamp) {
            try {
                // 使用EventSource直接连接
                this.addMessage('system', '尝试使用GET方式建立SSE连接...')

                // 构建URL，通过查询参数传递问题
                let esUrl = `${this.apiBaseUrl}/${endpoint}?t=${timestamp}&ask_content=${encodeURIComponent(this.question)}`
                if (this.chatId) {
                    esUrl += `&c_uuid=${encodeURIComponent(this.chatId)}`
                }

                console.log('GET方式SSE连接URL:', esUrl)
                this.addMessage('system', `连接URL: ${esUrl}`)

                // 创建EventSource连接
                this.eventSource = new EventSource(esUrl, { withCredentials: false })
                this.connectionStatus = '已连接到SSE服务 (GET方式)'

                this.setupEventListeners()
            } catch (e) {
                console.error('GET连接错误:', e)
                this.addMessage('error', `GET连接失败: ${e.message}`)
                this.stopStream()
            }
        },

        // 使用POST方式请求SSE
        async startStreamWithPost(endpoint, timestamp) {
            try {
                this.addMessage('system', '尝试使用POST方式建立SSE连接...')

                // 准备POST请求数据
                const requestData = {
                    ask_content: this.question,
                    askid: this.chatId || Date.now().toString()  // 使用chatId或时间戳作为askid
                }

                if (this.chatId) {
                    requestData.c_uuid = this.chatId
                }

                const url = `${this.apiBaseUrl}/${endpoint}?t=${timestamp}`
                console.log('POST方式SSE连接URL:', url)
                this.addMessage('system', `POST到: ${url}`)
                console.log('POST数据:', requestData)

                // 先发送POST请求
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestData),
                    credentials: 'omit',
                    mode: 'cors'
                })

                if (!response.ok) {
                    throw new Error(`HTTP错误: ${response.status}`)
                }

                this.addMessage('system', 'POST请求成功，建立SSE连接...')

                // 然后创建EventSource连接，添加askid参数
                const esUrl = `${this.apiBaseUrl}/${endpoint}?t=${timestamp + 1}&askid=${requestData.askid}`
                console.log('SSE连接URL:', esUrl)
                this.eventSource = new EventSource(esUrl, { withCredentials: false })
                this.connectionStatus = '已连接到SSE服务 (POST方式)'

                this.setupEventListeners()
            } catch (e) {
                console.error('POST连接错误:', e)
                this.addMessage('error', `POST连接失败: ${e.message}`)
                this.stopStream()
            }
        },

        // 设置EventSource事件监听器
        setupEventListeners() {
            // 处理消息
            this.eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    this.handleStreamMessage(data)

                    // 如果收到结束消息，关闭连接
                    if (data.type === 'finish_answer') {
                        this.stopStream()
                    }
                } catch (e) {
                    console.error('解析消息失败:', e)
                    this.addMessage('error', `解析消息失败: ${e.message}`)
                }
            }

            // 处理连接打开
            this.eventSource.onopen = () => {
                this.connectionStatus = `已连接，正在接收数据... (${this.requestMethod.toUpperCase()}方式)`
            }

            // 处理错误
            this.eventSource.onerror = (e) => {
                console.error('SSE错误:', e)
                this.addMessage('error', 'SSE连接错误或中断')
                this.stopStream()
            }
        },

        // 停止流式连接
        stopStream() {
            this.isStreaming = false
            this.connectionStatus = '已断开'
            this.stopTimer()

            if (this.eventSource) {
                this.eventSource.close()
                this.eventSource = null
            }

            this.addMessage('system', '连接已关闭')
        },

        // 开始计时器
        startTimer() {
            this.timerInterval = setInterval(() => {
                this.$forceUpdate() // 强制更新视图以刷新时间
            }, 1000)
        },

        // 停止计时器
        stopTimer() {
            if (this.timerInterval) {
                clearInterval(this.timerInterval)
                this.timerInterval = null
            }
        },

        // 清空所有消息
        clearMessages() {
            this.messages = []
            this.currentAnswer = ''
        },

        // 添加消息
        addMessage(type, content) {

            // console.log('添加消息:', type, content ,typeof content)
            // 确保content是字符串
            const messageContent = typeof content === 'object' 
                ? JSON.stringify(content, null, 2)
                : String(content || '')

            console.log('添加消息:', type, messageContent ,typeof messageContent)

            const message = {
                type,
                content: messageContent,
                time: new Date().toLocaleTimeString()
            }

            if (type === 'answer' && this.messages.length > 0 && this.messages[0].type === 'answer') {
                // 更新现有的回答消息
                this.messages[0].content = messageContent
            } else {
                // 添加新消息到顶部
                this.messages.unshift(message)
            }
        },

        // 处理流消息
        handleStreamMessage(data) {
            const type = data.type
            const payload = data.payload || {}

            // 在所有case语句之前声明可能的变量
            let chunkText = ''
            let content = ''

            // 确保payload.content存在且正确格式化
            if ('content' in payload) {
                if (typeof payload.content === 'object') {
                    content = JSON.stringify(payload.content, null, 2)
                } else {
                    content = String(payload.content)
                }
            }

            switch (type) {
                case 'system':
                    this.addMessage('system', content || '系统消息')
                    break

                case 'query_received':
                    this.addMessage('query', content || payload.ask_content || '问题已接收')
                    break

                case 'generation_start':
                    this.currentAskId = payload.uuid || ''
                    this.addMessage('status', content || '开始生成回答...')
                    break

                case 'retrieval_start':
                    this.addMessage('status', content || '开始检索相关内容...')
                    break

                case 'retrieval_end':
                    this.addMessage('status', content || '检索完成')
                    break

                case 'retrieved_chunk': {
                    let chunkContent = ''
                    if (typeof payload.content === 'object' && payload.content !== null) {
                        chunkContent = JSON.stringify(payload.content, null, 2)
                    } else {
                        chunkContent = String(payload.content)
                    }
                    chunkText = `检索片段 [${payload.chunk_id || '未知'}]:\n来源: ${payload.doc_name || '未知'} (第${payload.pageno || '?'}页)\n内容: ${chunkContent || '无内容'}`
                    this.addMessage('chunk', chunkText)
                    break
                }

                case 'generating_token': {
                    console.log('generating_token:', content, typeof content)
                    // 更新当前答案
                    if (typeof content === 'object' && content !== null) {
                        this.currentAnswer += JSON.stringify(content)
                    } else {
                        this.currentAnswer += String(content || '')
                    }
                    console.log('currentAnswer:', this.currentAnswer, typeof this.currentAnswer)

                    // 更新答案消息
                    if (this.messages.length > 0 && this.messages[0].type === 'answer') {
                        this.messages[0].content = this.currentAnswer
                        this.$forceUpdate() // 强制更新视图
                    } else {
                        this.addMessage('answer', this.currentAnswer)
                    }
                    break
                }

                case 'finish_answer':
                    this.currentAnswer = ''
                    this.addMessage('system', '回答完成')
                    break

                case 'error':
                    this.addMessage('error', `错误: ${content || '未知错误'}`)
                    break

                default:
                    this.addMessage('unknown', `未知消息类型: ${type}\n${JSON.stringify(data, null, 2)}`)
            }

            // 滚动到顶部
            this.$nextTick(() => {
                if (this.$refs.messagesContainer) {
                    this.$refs.messagesContainer.scrollTop = 0
                }
            })
        },

        // 获取消息类型的显示名称
        getMessageTypeName(type) {
            const typeNames = {
                system: '系统',
                query: '问题',
                answer: '回答',
                status: '状态',
                chunk: '检索',
                error: '错误',
                unknown: '未知'
            }

            return typeNames[type] || type
        },

        // 渲染消息内容
        renderContent(content) {
            if (!content) return ''
            
            try {
                // 如果是对象，转换为JSON字符串
                const text = typeof content === 'object' && content !== null
                    ? JSON.stringify(content, null, 2)
                    : String(content)

                console.log('renderContent处理前:', typeof text, text)
                
                // 首先尝试解析可能的JSON字符串
                try {
                    const parsed = JSON.parse(text)
                    if (typeof parsed === 'object' && parsed !== null) {
                        return `<pre>${JSON.stringify(parsed, null, 2)}</pre>`
                    }
                } catch (e) {
                    // 不是JSON字符串，继续正常处理
                }
                
                const html = marked(text)
                console.log('renderContent处理后:', typeof html, html)
                return html
            } catch (e) {
                console.error('渲染错误:', e)
                return String(content)
            }
        }
    },

    // 组件销毁前清理资源
    beforeDestroy() {
        this.stopStream()
    }
}
</script>

<style scoped>
.sse-test-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 20px;
    font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

h1 {
    text-align: center;
    margin-bottom: 20px;
    color: #333;
}

.input-section {
    background: #f9f9f9;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-group {
    margin-bottom: 15px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
    color: #555;
}

.radio-group {
    display: flex;
    gap: 20px;
}

.radio-group label {
    display: inline-flex;
    align-items: center;
    font-weight: normal;
    cursor: pointer;
}

.radio-group input {
    margin-right: 5px;
    width: auto;
}

input,
textarea {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
}

textarea {
    height: 100px;
    resize: vertical;
}

.buttons {
    display: flex;
    gap: 10px;
    margin-top: 15px;
}

button {
    padding: 10px 15px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
    transition: background-color 0.2s;
}

button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.primary-btn {
    background: #4CAF50;
    color: white;
}

.primary-btn:hover:not(:disabled) {
    background: #3e8e41;
}

.stop-btn {
    background: #f44336;
    color: white;
}

.stop-btn:hover {
    background: #d32f2f;
}

.clear-btn {
    background: #9e9e9e;
    color: white;
}

.clear-btn:hover {
    background: #757575;
}

.status-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    background: #eee;
    padding: 10px 15px;
    border-radius: 8px 8px 0 0;
    font-size: 14px;
}

.status-item {
    color: #555;
}

.message-area {
    height: 500px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 0 0 8px 8px;
}

.no-messages {
    text-align: center;
    color: #999;
    padding: 50px 0;
}

.messages {
    height: 100%;
    overflow-y: auto;
    padding: 15px;
}

.message {
    margin-bottom: 15px;
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid #ddd;
}

.message-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 12px;
    color: #666;
}

.message-type {
    font-weight: bold;
    text-transform: uppercase;
}

.message-content {
    background: rgba(255, 255, 255, 0.7);
    padding: 8px;
    border-radius: 4px;
    overflow-x: auto;
    word-break: break-word;
    line-height: 1.5;
}

/* 不同类型消息的样式 */
.message.system {
    background-color: #f8f9fa;
    border-left-color: #6c757d;
}

.message.answer {
    background-color: #e8f5e9;
    border-left-color: #4caf50;
}

.message.query {
    background-color: #e3f2fd;
    border-left-color: #2196f3;
}

.message.error {
    background-color: #ffebee;
    border-left-color: #f44336;
}

.message.status {
    background-color: #f5f5f5;
    border-left-color: #9e9e9e;
}

.message.chunk {
    background-color: #fff3e0;
    border-left-color: #ff9800;
}

.message.unknown {
    background-color: #fafafa;
    border-left-color: #9e9e9e;
}

@media (max-width: 768px) {
    .sse-test-container {
        padding: 10px;
    }

    .buttons {
        flex-direction: column;
    }

    .message-area {
        height: 400px;
    }
}

/* 添加Markdown样式 */
.markdown-body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    line-height: 1.6;
    word-wrap: break-word;
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
    margin-top: 24px;
    margin-bottom: 16px;
    font-weight: 600;
    line-height: 1.25;
}

.markdown-body h2 {
    padding-bottom: 0.3em;
    font-size: 1.5em;
    border-bottom: 1px solid #eaecef;
}

.markdown-body p {
    margin-top: 0;
    margin-bottom: 16px;
}

.markdown-body code {
    padding: 0.2em 0.4em;
    margin: 0;
    font-size: 85%;
    background-color: rgba(27, 31, 35, 0.05);
    border-radius: 3px;
}

.markdown-body pre {
    padding: 16px;
    overflow: auto;
    font-size: 85%;
    line-height: 1.45;
    background-color: #f6f8fa;
    border-radius: 3px;
}

/* 数学公式样式 */
.katex-display {
    margin: 1em 0;
    overflow-x: auto;
    overflow-y: hidden;
}

.katex {
    font-size: 1.1em;
}
</style>